import { useSelector } from "react-redux";
import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import AdminShell from "./AdminShell";
import { dialog } from "../Common/TreasuryDialog";

const API = process.env.REACT_APP_API_URL || "";

const PropertyExpenses = () => {
  const { propertyId } = useParams();
  const [property, setProperty] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showTypeForm, setShowTypeForm] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [form, setForm] = useState({ expenseTypeId: "", amount: "", date: "", description: "" });

  const { auth } = useSelector((state) => state);
  const token = auth?.data?.accesstoken || auth?.data?.access_token;
  const authHeader = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/property/getById/${propertyId}`, { headers: authHeader }).then((r) => r.json()),
      fetch(`${API}/api/admin/expenses/${propertyId}`, { headers: authHeader }).then((r) => r.json()),
      fetch(`${API}/api/admin/expense-types`, { headers: authHeader }).then((r) => r.json()),
    ])
      .then(([propData, expData, typesData]) => {
        setProperty(propData.property || propData);
        setExpenses(expData.expenses || []);
        setTotalAmount(expData.totalAmount || 0);
        setExpenseTypes(typesData.types || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [propertyId, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAddType = async (e) => {
    e.preventDefault();
    if (!newTypeName.trim()) return;
    const res = await fetch(`${API}/api/admin/expense-types`, {
      method: "POST",
      headers: { ...authHeader, "Content-Type": "application/json" },
      body: JSON.stringify({ name: newTypeName.trim() }),
    });
    const data = await res.json();
    if (data.status === 1) {
      setNewTypeName("");
      setShowTypeForm(false);
      loadData();
    } else {
      dialog.error("Error", data.msg);
    }
  };

  const handleDeleteType = async (id) => {
    const confirmed = await dialog.confirm("Delete Type", "Delete this expense type?");
    if (!confirmed) return;
    await fetch(`${API}/api/admin/expense-types/${id}`, { method: "DELETE", headers: authHeader });
    loadData();
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!form.expenseTypeId || !form.amount || !form.date) {
      dialog.error("Validation", "Type, amount, and date are required.");
      return;
    }
    const res = await fetch(`${API}/api/admin/expenses`, {
      method: "POST",
      headers: { ...authHeader, "Content-Type": "application/json" },
      body: JSON.stringify({ propertyId, ...form, amount: parseFloat(form.amount) }),
    });
    const data = await res.json();
    if (data.status === 1) {
      dialog.success("Success", data.msg);
      setShowForm(false);
      setForm({ expenseTypeId: "", amount: "", date: "", description: "" });
      loadData();
    } else {
      dialog.error("Error", data.msg);
    }
  };

  const handleDeleteExpense = async (id) => {
    const confirmed = await dialog.confirm("Delete Expense", "Delete this expense entry?");
    if (!confirmed) return;
    await fetch(`${API}/api/admin/expenses/${id}`, { method: "DELETE", headers: authHeader });
    loadData();
  };

  return (
    <AdminShell pageTitle={`Expenses — ${property?.title || "Property"}`}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#111827" }}>{property?.title}</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>{property?.city} · Expense tracking</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => setShowTypeForm(!showTypeForm)}
            style={{
              padding: "9px 18px",
              background: "#f3f4f6",
              color: "#374151",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Manage Types
          </button>
          <button
            onClick={() => setShowForm(!showForm)}
            style={{
              padding: "9px 18px",
              background: "linear-gradient(135deg,#c9a84c,#7a5c10)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {showForm ? "Cancel" : "+ Add Expense"}
          </button>
        </div>
      </div>

      {/* Expense Types manager */}
      {showTypeForm && (
        <div style={{ background: "#fff", borderRadius: 12, padding: 20, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", marginBottom: 20 }}>
          <h4 style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 700, color: "#111827" }}>Expense Types</h4>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
            {expenseTypes.map((t) => (
              <span
                key={t._id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: "#f3f4f6",
                  borderRadius: 20,
                  padding: "4px 12px",
                  fontSize: 13,
                  color: "#374151",
                }}
              >
                {t.name}
                <button
                  onClick={() => handleDeleteType(t._id)}
                  style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", padding: 0, fontSize: 14, lineHeight: 1 }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <form onSubmit={handleAddType} style={{ display: "flex", gap: 8 }}>
            <input
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              placeholder="New type name (e.g. Maintenance, Insurance)"
              style={{ flex: 1, padding: "8px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13 }}
            />
            <button
              type="submit"
              style={{
                padding: "8px 18px",
                background: "#0e3725",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Add
            </button>
          </form>
        </div>
      )}

      {/* Add expense form */}
      {showForm && (
        <div style={{ background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", marginBottom: 20 }}>
          <h4 style={{ margin: "0 0 18px", fontSize: 14, fontWeight: 700, color: "#111827" }}>New Expense</h4>
          <form onSubmit={handleAddExpense}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>Type *</label>
                <select
                  value={form.expenseTypeId}
                  onChange={(e) => setForm((f) => ({ ...f, expenseTypeId: e.target.value }))}
                  style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13 }}
                >
                  <option value="">— Select type —</option>
                  {expenseTypes.map((t) => (
                    <option key={t._id} value={t._id}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>Amount (SAR) *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  placeholder="e.g. 5000"
                  style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>Date *</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                  style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }}
                />
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5 }}>Description (optional)</label>
              <input
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Brief description"
                style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13, boxSizing: "border-box" }}
              />
            </div>
            <button
              type="submit"
              style={{
                padding: "10px 24px",
                background: "linear-gradient(135deg,#c9a84c,#7a5c10)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Save Expense
            </button>
          </form>
        </div>
      )}

      {/* Summary card */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
        {[
          { label: "Total Expenses", value: `SAR ${totalAmount.toLocaleString()}`, color: "#dc2626" },
          { label: "Expense Entries", value: expenses.length, color: "#374151" },
          { label: "Expense Types", value: expenseTypes.length, color: "#374151" },
        ].map((s) => (
          <div key={s.label} style={{ background: "#fff", borderRadius: 10, padding: "18px 20px", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Expenses table */}
      <div style={{ background: "#fff", borderRadius: 12, padding: 28, boxShadow: "0 1px 8px rgba(0,0,0,0.07)" }}>
        <h3 style={{ margin: "0 0 20px", fontSize: 15, fontWeight: 700, color: "#111827" }}>Expense Log</h3>
        {loading ? (
          <div style={{ textAlign: "center", color: "#9ca3af", padding: "40px 0" }}>Loading…</div>
        ) : expenses.length === 0 ? (
          <div style={{ textAlign: "center", color: "#9ca3af", padding: "40px 0", fontSize: 14 }}>
            No expenses recorded yet
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #f3f4f6" }}>
                <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Date</th>
                <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Type</th>
                <th style={{ textAlign: "right", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Amount (SAR)</th>
                <th style={{ textAlign: "left", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Description</th>
                <th style={{ textAlign: "center", padding: "8px 12px", color: "#6b7280", fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((ex) => (
                <tr key={ex._id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "12px 12px", color: "#374151" }}>
                    {new Date(ex.date).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}
                  </td>
                  <td style={{ padding: "12px 12px" }}>
                    <span style={{ background: "#f3f4f6", color: "#374151", padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                      {ex.expenseTypeId?.name || "—"}
                    </span>
                  </td>
                  <td style={{ padding: "12px 12px", textAlign: "right", fontWeight: 700, color: "#dc2626" }}>
                    {ex.amount.toLocaleString()}
                  </td>
                  <td style={{ padding: "12px 12px", color: "#6b7280", fontSize: 12 }}>{ex.description || "—"}</td>
                  <td style={{ padding: "12px 12px", textAlign: "center" }}>
                    <button
                      onClick={() => handleDeleteExpense(ex._id)}
                      style={{
                        padding: "5px 12px",
                        background: "#fee2e2",
                        color: "#dc2626",
                        border: "none",
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminShell>
  );
};

export default PropertyExpenses;
